import { COMP_ID_DATA_ATTR, DEFAULT_FETCH_TIMEOUT, DEFAULT_FETCH_MAX_RETRIES } from '../utils/consts'
import { FetchOptions } from '../types'
import {
	ExternalComponentBundlingError,
	ExternalComponentRendererError,
	ExternalComponentTimeoutError,
	ExternalComponentFetchError,
	ExternalComponentFileNotFoundError,
} from '../errors'

export const activateMessagePort = (port: MessagePort) => {
	port.onmessage = () => {}
}

export const waitForElementByRemoteId = (id: string): Element | null => {
	if (!id) {
		throw new Error('Cannot resolve element with empty id')
	}

	const CHECK_INTERVAL_MS = 5
	const GET_INSTANCE_TIMEOUT_MS = 100

	const startTime = Date.now()
	let prevCheckTime = 0

	let element: Element | null = null

	do {
		if (Date.now() - prevCheckTime > CHECK_INTERVAL_MS) {
			element = getElementByRemoteId(id)
			prevCheckTime = Date.now()
		}
	} while (!element && Date.now() - startTime < GET_INSTANCE_TIMEOUT_MS)

	return element
}

export const getElementByRemoteId = (id: string): Element | null => {
	if (!id) {
		throw new Error('Cannot get element with empty id')
	}

	// Partytown might throw an error if we try to access an instance which no longer exists on the document,
	// so we need to wrap querySelector with try/catch. Browser "querySelector" never throws an error.
	try {
		return document.querySelector(`[${COMP_ID_DATA_ATTR}="${id}"]`) ?? null
	} catch (e) {
		console.error('Failed to get element by remote id: ' + id)
		return null
	}
}

export const fetchWithTimeoutRetries = async (
	url: string,
	modulePath: string,
	options: FetchOptions = {},
	retryCount = 0
): Promise<Response> => {
	const {
		maxRetries = DEFAULT_FETCH_MAX_RETRIES,
		timeout = DEFAULT_FETCH_TIMEOUT,
		fetchFn = fetch,
		...fetchOptions
	} = options

	const abortController = new AbortController()
	const { signal } = abortController
	const abort = () => abortController.abort()

	const timeoutId = setTimeout(() => {
		abort()
	}, timeout)

	try {
		const response = await fetchFn(url, {
			...fetchOptions,
			signal,
		})

		clearTimeout(timeoutId)

		if (response.ok) {
			return response
		} else {
			if (response.status === 404) {
				throw new ExternalComponentFileNotFoundError(modulePath)
			} else if (response.status === 409) {
				// TODO on TB-7658 replace second arg with the detailed error
				throw new ExternalComponentBundlingError(modulePath, '')
			} else {
				throw new Error(`Failed to fetch "${url}" with status ${response.status}`)
			}
		}
	} catch (e) {
		clearTimeout(timeoutId)

		if (signal.aborted) {
			if (retryCount < maxRetries) {
				return fetchWithTimeoutRetries(url, modulePath, options, retryCount + 1)
			} else {
				throw new ExternalComponentTimeoutError(`Failed to fetch ${url} with timeout ${timeout}ms`)
			}
		} else {
			throw e instanceof ExternalComponentRendererError ? e : new ExternalComponentFetchError(e?.message, e)
		}
	}
}

export const withTimeout = (timeoutMs: number) => (promiseFn: Function) => (...args: Array<any>) => {
	const promise = promiseFn(...args)
	const timeout = new Promise((_, reject) => {
		setTimeout(
			() => reject(new ExternalComponentTimeoutError(`${promiseFn.name} timed out after ${timeoutMs}ms`)),
			timeoutMs
		)
	})
	return Promise.race([promise, timeout])
}
